import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { BottomNavComponent } from './shared/components/bottom-nav/bottom-nav.component';
import { FocusHeaderComponent } from './shared/components/focus-header/focus-header.component';
import { WalletService } from './core/services/wallet.service';
import { AuthService } from './core/auth/auth.service';
import { MerchantService } from './core/auth/merchant.service';
import { IntroducerStateService } from './features/registration/customers-onboarding/services/introducer-state.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [BottomNavComponent, FocusHeaderComponent, RouterOutlet, SidebarComponent, HeaderComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  wallet = inject(WalletService);
  private auth = inject(AuthService);
  private router = inject(Router);

  private readonly currentUrl = signal<string>(this.router.url);

  readonly showChrome = computed(() => {
    if (!this.auth.isLoggedIn()) return false;
    // Onboarding has no merchant behind it yet, so the sidebar would link to screens that cannot
    // load; /login-failed is pre-auth by definition. The payment pages are what a payer sees, so a
    // merchant opening their own link sees exactly that, not their dashboard around it.
    const url = this.currentUrl();
    return !['/login-failed', '/onboarding', '/sign-up', '/register', '/access-request', '/pay/', '/shop/']
      .some(prefix => url.startsWith(prefix));
  });

  /**
   * A client filling in their application sees only the application: a slim header, the section
   * list and the save bar. The product's menus, balance and account toggle belong to an account
   * that isn't open yet. Introducers keep the full shell; they move between clients and their own
   * referral pages from here.
   */
  readonly focusMode = computed(() =>
    this.showChrome() && this.currentUrl().startsWith('/application/form') && !this.isIntroducer());

  private readonly isIntroducer = toSignal(inject(IntroducerStateService).isIntroducer$, { initialValue: false });

  /** Until the application is approved the phone tab bar has nothing to offer: every tab is locked. */
  readonly awaitingApproval = inject(MerchantService).awaitingApproval;

  constructor() {
    this.auth.init();

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.currentUrl.set(e.urlAfterRedirects);
        this.wallet.closeMobileSidebar();
      });
  }
}
